/**
 * Locale-aware date and number formatting.
 *
 * Formatting follows the *browser* locale (the user's regional settings),
 * not the in-app language picker. Language and region are different things:
 * the picker offers no regional variants — there is no "English (US)" entry,
 * only a single `en` — so it cannot express a date or number format.
 *
 * Built on the native `Intl` API. Angular's `date`/`number` pipes are not
 * used because they fall back to `en-US` unless the locale is registered via
 * `registerLocaleData` at build time, which cannot cover an arbitrary browser
 * locale — only the ones compiled into the bundle.
 */

/** Formatter caches — constructing an Intl formatter is expensive. */
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const numberFormatters = new Map<string, Intl.NumberFormat>();

/**
 * The browser's regional locale, e.g. "en-GB", "es-ES", "de-DE".
 *
 * `navigator.languages[0]` is preferred over `navigator.language` because it
 * reflects the full ordered preference list the user configured.
 */
export function browserLocale(): string {
  return navigator.languages?.[0] || navigator.language;
}

/** Date + time formatter for the current browser locale. */
function dateTimeFormatter(): Intl.DateTimeFormat {
  const locale = browserLocale();
  let formatter = dateTimeFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    dateTimeFormatters.set(locale, formatter);
  }
  return formatter;
}

/**
 * Formats a timestamp as a short date and time in the browser's locale.
 *
 * Replaces Angular's `date:'short'`. Empty input renders as an empty string;
 * an unparseable value is logged and rendered empty rather than breaking the
 * surrounding view.
 */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') { return ''; }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    console.error('formatDateTime: unparseable timestamp', value);
    return '';
  }
  return dateTimeFormatter().format(date);
}

/**
 * Parses an Angular `digitsInfo` string ("minInt.minFrac-maxFrac") into
 * Intl.NumberFormat options.
 *
 * Throws on a malformed value: that is a coding error, not user input.
 */
function parseDigitsInfo(digitsInfo: string): Intl.NumberFormatOptions {
  const match = /^(\d+)\.(\d+)-(\d+)$/.exec(digitsInfo);
  if (!match) {
    throw new Error(`Invalid digitsInfo "${digitsInfo}" — expected "minInt.minFrac-maxFrac"`);
  }
  return {
    minimumIntegerDigits: Number(match[1]),
    minimumFractionDigits: Number(match[2]),
    maximumFractionDigits: Number(match[3]),
  };
}

/**
 * Formats a number in the browser's locale, so decimal and thousands
 * separators follow the user's regional settings (1,234.56 vs 1.234,56).
 *
 * Replaces Angular's `number` pipe; `digitsInfo` uses the same syntax.
 */
export function formatNumber(
  value: number | null | undefined,
  digitsInfo: string,
): string {
  if (value === null || value === undefined || isNaN(value)) { return ''; }
  const locale = browserLocale();
  const key = `${locale}|${digitsInfo}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, parseDigitsInfo(digitsInfo));
    numberFormatters.set(key, formatter);
  }
  return formatter.format(value);
}
